import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, Input, OnDestroy, Type} from '@angular/core';
import { Router } from '@angular/router';

import { Subscription, of, filter, BehaviorSubject, catchError, take, tap, iif, EMPTY } from 'rxjs';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MediaSubscriptionService } from '../../../../Monitor/Core/services/media-subscription.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { GaugeChartModel } from '../../../../Common/Charts/models/gaugeChart/gauge-chart.model';
import { QuotaInformation } from '../../../../api/mediaSubscriptions/models/quota-information.api.model';
import { QuotaHistoryInformation } from '../../../../api/mediaSubscriptions/models/quota-history-information.api.model';
import { SeriesModel } from '../../../../Common/Charts/models/series.model';
import { GaugeChartOptionsModel } from '../../../../Common/Charts/models/gaugeChart/gauge-chart-options.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateInputTypesEnum } from '../../../../Common/Forms/enums/date-input-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { RelativeTimezoneDateTypeEnum } from '../../../../api/mentions/enum/relative-timezone-date-type.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { QuotaUsageWidgetComponent } from '../QuotaUsageWidget/quota-usage-widget.component';
import { MediaSubscriptionQuotaUsageWidgetComponent } from '../QuotaUsageWidget/media-subscription-quota-usage-widget.component';
import { SocialListeningQuotaUsageWidgetComponent } from '../QuotaUsageWidget/social-listening-quota-usage-widget.component';
import { ThemeService } from '../../../Intelligence/services/theme.service';
import { AgentWidgetComponent, AgentWidgetMode } from '../../../Intelligence/components/AgentWidget/agent-widget.component';
import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SignificantDigitsPipe } from '../../../../Common/Misc/pipes/SignificantDigits/significant-digits.pipe';
import { sortArray } from '../../../../Common/Misc/helpers/sort/sort.helper';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { MonitoringQuotaTypesEnum } from '../../../../api/mentions/enum/monitoring-quota-types.enum';

interface QuotaUsageDetail {
    item: QuotaHistoryInformation & {
        socialType: string;
    };
    collased: boolean;
    hasBackFills: boolean;
    totalCount: number;
}

@Component({
    selector: 'agpr-quota-usage-dashboard',
    templateUrl: './quota-usage-dashboard.component.html',
    styleUrls: ['./quota-usage-dashboard.component.scss'],
    animations: [
        trigger('containerState', [
            state('collapsed', style({ height: '60px' })),
            state('expanded', style({ height: '*' })),
            transition('collapsed <=> expanded', [
                animate('220ms ease-in-out')
            ])
        ])
    ],
    standalone: false
})
export class QuotaUsageDashboardComponent implements OnDestroy, AfterViewInit {

    @Input() expanded: boolean = true;
    @Input() showBanner: boolean = true;
    @Input() showHeader: boolean = true;
    @Input() selectedStartDate: Date;

    private _quotaType?: MonitoringQuotaTypesEnum;

    // When set, scopes both the gauge/totals (via resolveQuotaInfo) and the breakdown list (via filteredQuotaDetails) to this quota type.
    @Input()
    set quotaType(value: MonitoringQuotaTypesEnum | undefined) {
        this._quotaType = value;
        this.headerLabelKey = value === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION
            ? 'Social.intelligence.themeManagement.quotaUsagePanel.headerLabel.mediaSubscription'
            : 'Social.intelligence.themeManagement.quotaUsagePanel.headerLabel';
    }
    get quotaType(): MonitoringQuotaTypesEnum | undefined {
        return this._quotaType;
    }

    public headerLabelKey: string = 'Social.intelligence.themeManagement.quotaUsagePanel.headerLabel';
    public filteredQuotaDetails: QuotaUsageDetail[] = [];

    private subscriptions: Subscription[] = [];
    public readonly buttonTypes = ButtonTypesEnum;
    public isPinned: boolean;

    public dailyAverageUsageChart: GaugeChartModel;
    public dailyAverageUsageChartOptions: GaugeChartOptionsModel;
    public quotaInfo: QuotaInformation = null;
    public quotaDetails: QuotaUsageDetail[] = [];

    public totalQuota: string;
    public totalQuotaUsed: string;
    public totalQuotaUsedPercentage: string;
    public daysRemaining: number;
    public quotaResetDate: string;

    public dateFilterSelected: DateQueryModel = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.THIS_MONTH);
    public quotaDateQueryInputTopicOptions: DropdownOptionModel[] = [];
    public socialMediaMap: Record<number, number[]> = {}; // socialId, themeIds

    // loading status
    public socailThemedIdsLoading = true;
    public quotaLoading = true;

    private dateRangeReady = false;
    private viewInitCalled = false;
    private dateFormat: string;

    readonly messageSeverityEnum = messageSeverityEnum;

    public animationTrigger = new BehaviorSubject(
        new FullProfileAnimationModel(
            FullProfileDialogAnimationsEnum.slideInOutAnimation,
            OverlayAnimationStateEnum.open
        )
    );

    public allActions = [
        {
            label: this.localizationService.transform('Social.intelligence.themeManagement.quotaBreakdownPanel.button.edit'),
            command: (event: any | { data: QuotaHistoryInformation }) => this.editCustomer(event.data)
        },
        {
            label: this.localizationService.transform('Social.intelligence.themeManagement.quotaBreakdownPanel.button.view'),
            command: (event: any | { data: QuotaHistoryInformation }) => this.viewCustomer(event.data)
        }
    ];

    constructor(
        private router: Router,
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public currentUserStateService: CurrentUserStateService,
        public themeService: ThemeService,
        public messageService: MessageService,
        public significantDigitsPipe: SignificantDigitsPipe,
        public agentService: AgentService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x))
                .pipe(take(1)).subscribe(prefs => {
                    this.dateFormat = prefs.getDateFormatString(true);
                    this.getLast12MonthlyRanges();
                }),
            this.themeService.getQuotaPanelVisibility().subscribe((pinnedState) => {
                this.isPinned = pinnedState;
            }),


        );
    }

    ngAfterViewInit(): void {
        this.subscriptions.push(
            iif(() => this.expanded, this.agentService.all$, of([]))
                .pipe(
                    tap(() => this.socailThemedIdsLoading = true),
                    catchError(() => {
                        this.socailThemedIdsLoading = false;
                        return EMPTY;
                    })
                )
                .subscribe((list: MediaSubscription[]) => {
                    this.socialMediaMap = list.reduce((cur, item) => {
                        if (item?.themeIds && item?.themeIds.length > 0) {
                            cur[item.id] = item.themeIds;
                        }
                        return cur;
                    }, {});
                    this.socailThemedIdsLoading = false;
                })
        );
        this.viewInitCalled = true;
        this.initDateSelection();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe);
    }

    private resolveQuotaInfo(quotaInfoList: QuotaInformation[]): QuotaInformation {
        const emptyQuotaInfo: QuotaInformation = { currentCount: 0, maxCount: 0, additionalCount: 0, enabled: false, nextResetDate: '' };
        return quotaInfoList?.find(info => info.quotaType === this.quotaType) ?? emptyQuotaInfo;
    }

    editCustomer(detail: QuotaHistoryInformation) {
        const id = detail.subId;

        if (detail.isSocial) {
            this.mediaSubscriptionService.getById(detail.subId).subscribe((subscription: MediaSubscription) => {
                const io = {
                    inputs: {
                        animationTrigger: this.animationTrigger,
                        overlayContainer: document.getElementById('main-content'),
                        agentModel: subscription,
                        mode: AgentWidgetMode.edit
                    },
                    outputs: {
                        dialogClose: () => {
                            this.calculateAccountStats();
                        }
                    }
                };
                this.coreDialogService.open(AgentWidgetComponent, io);
            });
        } else {
            this.router.navigate(['Monitoring/subscriptions/subscription/', id, 'update']);
        }
    }

    // TODO : NEP-34391
    // Update Theme and Subscript pages to support selected topic and date range
    viewCustomer(detail: QuotaHistoryInformation) {
        const id = detail.subId;
        if (detail.isSocial) {
            const firstTheme = this.socialMediaMap[id]?.[0] ?? null;
            if (firstTheme) {
                this.router.navigate(['Social/themes/theme', firstTheme, 'analytics']);
            } else {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Social.intelligence.themeNotFound.heading',
                    detail: 'Social.intelligence.themeNotFound.message'
                });
            }
        } else {
            this.router.navigate(['Monitoring/subscriptions/subscription/', id]);
        }
    }

    toggleQuotaUsageWidget(): void {
        const { component, key } = this.getExpandedWidget();
        if (this.expanded) {
            this.coreDialogService.close(key);
        } else {
            const io = {
                inputs: {
                    animationTrigger: this.animationTrigger,
                    overlayContainer: document.getElementById('main-content'),
                    selectedStartDate: this.dateFilterSelected.absoluteRange.start
                },
                outputs: {}
            };
            this.coreDialogService.open(component, io);
        }
    }

    private getExpandedWidget(): { component: Type<QuotaUsageWidgetComponent>; key: string } {
        switch (this.quotaType) {
            case MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION:
                return { component: MediaSubscriptionQuotaUsageWidgetComponent, key: MediaSubscriptionQuotaUsageWidgetComponent.DIALOG_KEY };
            case MonitoringQuotaTypesEnum.SOCIAL_LISTENING:
                return { component: SocialListeningQuotaUsageWidgetComponent, key: SocialListeningQuotaUsageWidgetComponent.DIALOG_KEY };
            default:
                return { component: QuotaUsageWidgetComponent, key: QuotaUsageWidgetComponent.DIALOG_KEY };
        }
    }

    calculateAccountStats(): void {
        const startTimestamp = this.dateFilterSelected.absoluteRange.start.valueOf(); // UTC timestamp
        const isActivePeriod = this.isActivePeriod();
        const requestTimeStamp = isActivePeriod ? null : startTimestamp;
        this.mediaSubscriptionService.getUsageHistory(requestTimeStamp, this.expanded).pipe(
            catchError(() => {
                return of<[QuotaInformation[], QuotaHistoryInformation[]]>([[{
                    currentCount: 0,
                    maxCount: 0,
                    additionalCount: 0,
                    enabled: false,
                    nextResetDate: ''
                }], []]);
            })
        ).subscribe(([quotaList, quotaDetails]) => {
            this.quotaInfo = this.resolveQuotaInfo(quotaList);
            const format = this.dateFormat;
            this.quotaDetails = quotaDetails.map<QuotaUsageDetail>((item: QuotaHistoryInformation) => {
                const socialTypeLabel = item.isSocial
                    ? 'Social.intelligence.themeManagement.quotaBreakdownPanel.type.social'
                    : 'Social.intelligence.themeManagement.quotaBreakdownPanel.type.subscription';

                const quotaHistory = isActivePeriod ? item.quotaGroup : item.quotaHistory;

                return {
                    item: {
                        ...item,
                        socialType: this.localizationService.transform(socialTypeLabel),
                        quotaHistory,
                        backfills: item.backfills.map((backFill) => ({
                            ...backFill,
                            backfillBeginDate: backFill.backfillBeginDate
                                ? `(${moment(backFill.backfillBeginDate).utc().format(format)})`
                                : ''
                        }))
                    },
                    collased: true,
                    hasBackFills: item?.backfills?.length > 0,
                    totalCount: (quotaHistory?.liveCount || 0) + (quotaHistory?.backfillCount || 0)
                };

            });

            sortArray(this.quotaDetails, 'totalCount', SortOrderEnum.descending);

            this.filteredQuotaDetails = this.quotaType
                ? this.quotaDetails.filter(detail => detail.item.isSocial === (this.quotaType === MonitoringQuotaTypesEnum.SOCIAL_LISTENING))
                : this.quotaDetails;

            this.calculateQuotaStats();
            this.generateDailyAverageUsageChart();
            this.quotaLoading = false;
        });

    }

    private initDateSelection() {
        if (this.dateRangeReady && this.viewInitCalled) { // Check input selectedStartDate value bound
            let initIndex = 0;

            if (this.selectedStartDate) {
                const selectedIndex = this.quotaDateQueryInputTopicOptions.findIndex(option => {
                    return option.value.query.absoluteRange.start.valueOf() === this.selectedStartDate.valueOf();
                });
                if (selectedIndex > -1) {
                    initIndex = selectedIndex;
                }
            }

            this.dateFilterSelected = this.quotaDateQueryInputTopicOptions[initIndex].value.query;
            this.calculateAccountStats();
        }
    }

    private getLast12MonthlyRanges() {
        const UTC_TIMEZONE = 'UTC';
        const EARLIEST_MONTH_AVAILABLE = '2026-03-01'; // March 2026: earliest month to include in queries

        const earliestMonth = moment(EARLIEST_MONTH_AVAILABLE).tz(UTC_TIMEZONE);
        const current = moment().tz(UTC_TIMEZONE);

        const dropdownOptions: DropdownOptionModel[] = [];

        for (let i = 0; i < 12; i++) {
            const start = current.clone().subtract(i, 'months').startOf('month'); // first day 00:00:00
            const end = start.clone().endOf('month');                             // last day 23:59:59.999

            // Break if start is earlier than earliestMonth
            if (start.isBefore(earliestMonth, 'month')) {
                break;
            }

            const queryModel = DateQueryModel.makeAbsoluteRange({
                type: DateQueryTypeEnum.ABSOLUTE,
                absoluteRange: {
                    start: start.toDate(),
                    end: end.toDate(),
                }

            }, UTC_TIMEZONE);

            const periodTo = this.localizationService.transform('Common.to');
            const formattedStart = moment(queryModel.absoluteRange.start).format(this.dateFormat);
            const formattedEnd = moment(queryModel.absoluteRange.end).format(this.dateFormat);
            const currentActiveLable = 'Social.intelligence.themeManagement.quotaUsagePanel.pinButton.activePeriod';

            const label =
                i === 0
                    ? this.localizationService.transform(currentActiveLable)
                    : `${formattedStart} ${periodTo} ${formattedEnd}`;

            dropdownOptions.push(new DropdownOptionModel(
                label,
                {
                    query: queryModel,
                    type: DateInputTypesEnum.useValue
                }
            ));
        }


        this.quotaDateQueryInputTopicOptions = dropdownOptions;
        this.dateRangeReady = true;
        this.initDateSelection();
    }



    // Returns true if this period is the first item in the date range
    private isActivePeriod() {
        const dateRangeStartDate = this.quotaDateQueryInputTopicOptions[0].value.query.absoluteRange.start;
        const selectedDate = this.dateFilterSelected.absoluteRange.start;
        return dateRangeStartDate.valueOf() === selectedDate.valueOf();
    }



    /**
     * Calculates the percentDailyQuotaUsed and constructs a chart with the data
     */
    private generateDailyAverageUsageChart(): void {
        this.dailyAverageUsageChart = null;

        const quotaInfo = this.quotaInfo;
        const selectedMonth = moment(this.dateFilterSelected.absoluteRange.start).utc();
        const daysInMonth = selectedMonth.daysInMonth();
        const daysElapsed = this.isActivePeriod() ? moment().date() : daysInMonth;
        const dailyMaxQuota = (quotaInfo.maxCount + quotaInfo.additionalCount) / daysInMonth;
        const dailyUsedQuota = quotaInfo.currentCount / daysElapsed;
        let percentDailyQuotaUsed;
        if (this.isBlankMediaSubscriptionQuotaPeriod()) {
            percentDailyQuotaUsed = 100;
        } else {
            percentDailyQuotaUsed = dailyMaxQuota === 0 ?
                0 : (dailyUsedQuota / dailyMaxQuota) * 100;
        }
        const chartSeries: SeriesModel[] = [
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            label: this.localizationService.transform('Social.intelligence.themeManagement.quotaUsagePanel.dailyAverageUsageChart.label'),
                            xValue: percentDailyQuotaUsed
                        }
                    ]
                )
            }
        ];

        this.dailyAverageUsageChartOptions = {
            minXDegrees: -210,
            maxXDegrees: 30,
            percentLabelOptions: {
                fontSize: 25,
                dy: -15
            },
            seriesLabelOptions: {
                fontSize: 14,
                dy: 30,
                textAlign: 'middle'
            }
        };

        this.dailyAverageUsageChart = new GaugeChartModel(chartSeries, this.dailyAverageUsageChartOptions);
    }

    private calculateQuotaStats(): void {
        if (!this.dateFormat) {
            return;
        }
        if (this.isBlankMediaSubscriptionQuotaPeriod()) {
            this.totalQuotaUsed = this.significantDigitsPipe.transform(this.filteredQuotaDetails.reduce((x, y) => x + (y.totalCount || 0), 0));
            this.totalQuota = this.totalQuotaUsed;
            this.totalQuotaUsedPercentage = '100%';
            this.daysRemaining = 0;
        } else {
            const totalQuota = this.quotaInfo.maxCount + this.quotaInfo.additionalCount;
            const totalQuotaUsed = this.quotaInfo.currentCount;
            this.totalQuota = this.significantDigitsPipe.transform(totalQuota);
            this.totalQuotaUsed = this.significantDigitsPipe.transform(totalQuotaUsed);
            this.totalQuotaUsedPercentage = totalQuota === 0 ? '0' : Math.round(totalQuotaUsed / totalQuota * 100) + '%';
            this.daysRemaining = this.isActivePeriod() ? moment(this.quotaInfo.nextResetDate).diff(moment(), 'days') : 0;
        }
        this.quotaResetDate = this.quotaInfo.nextResetDate
            ? moment(this.quotaInfo.nextResetDate).utc().format(this.dateFormat)
            : '';
    }

    private isBlankMediaSubscriptionQuotaPeriod(): boolean {
        return this.quotaType === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION && !this.quotaInfo.maxCount;
    }
}

